import { useState, useEffect } from 'react';
import { MapPin, Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import AddressForm, { AddressFormData } from './AddressForm';
import { toast } from 'sonner';

export interface Address {
  id: string;
  user_id: string;
  label: string;
  recipient_name: string;
  phone: string;
  street_name: string;
  street_number: string;
  details: string;
  city: string;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
}

const AddressBook = () => {
  const { user } = useAuth();
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editAddress, setEditAddress] = useState<Address | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      fetchAddresses();
    }
  }, [user]);

  const fetchAddresses = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('addresses')
        .select('*')
        .eq('user_id', user.id)
        .order('last_used_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAddresses(data || []);
    } catch (error) {
      console.error('Error fetching addresses:', error);
      toast.error('שגיאה בטעינת הכתובות');
    } finally {
      setLoading(false);
    }
  };

  const saveAddress = async (data: AddressFormData) => {
    if (!user) return;

    setSaving(true);
    try {
      if (editAddress) {
        // Update existing address
        const { error } = await supabase
          .from('addresses')
          .update(data)
          .eq('id', editAddress.id);

        if (error) throw error;
      } else {
        // Create new address
        const { error } = await supabase
          .from('addresses')
          .insert({ ...data, user_id: user.id });

        if (error) throw error;
      }

      setModalOpen(false);
      setEditAddress(null);
      fetchAddresses();
    } catch (error) {
      console.error('Error saving address:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const deleteAddress = async (id: string) => {
    try {
      const { error } = await supabase
        .from('addresses')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('כתובת נמחקה בהצלחה');
      fetchAddresses();
    } catch (error) {
      console.error('Error deleting address:', error);
      toast.error('שגיאה במחיקת הכתובת');
    }
  };

  const openEditModal = (address: Address) => {
    setEditAddress(address);
    setModalOpen(true);
  };

  const openCreateModal = () => {
    setEditAddress(null);
    setModalOpen(true);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">טוען כתובות...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card dir="rtl">
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            ניהול כתובות
          </CardTitle>
          {addresses.length > 0 && (
            <Button onClick={openCreateModal}>
              <Plus className="h-4 w-4 ml-2" />
              הוסף כתובת
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {addresses.length === 0 ? (
            <div className="text-center py-8">
              <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">אין כתובות שמורות עדיין</p>
              <Button onClick={openCreateModal}>
                <Plus className="h-4 w-4 ml-2" />
                הוסף כתובת ראשונה
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {addresses.map((address) => (
                <div key={address.id} className="border rounded-lg p-4 relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 left-2"
                    onClick={() => deleteAddress(address.id)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      <span className="font-medium">{address.label}</span>
                    </div>

                    <div>
                      <p className="font-medium">{address.recipient_name}</p>
                      <p className="text-sm text-muted-foreground">{address.phone}</p>
                    </div>

                    <div>
                      <p className="text-sm">
                        {address.street_name} {address.street_number}, {address.city}
                      </p>
                      {address.details && (
                        <p className="text-sm text-muted-foreground">{address.details}</p>
                      )}
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditModal(address)}
                      >
                        <Edit className="h-3 w-3 ml-1" />
                        עריכה
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editAddress ? 'עריכת כתובת' : 'הוספת כתובת חדשה'}
            </DialogTitle>
          </DialogHeader>
          <AddressForm
            initialData={editAddress || undefined}
            onSave={saveAddress}
            onCancel={() => setModalOpen(false)}
            isSaving={saving}
            type="billing"
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddressBook;